import { useEffect, useRef, useState } from "react";
import { Input } from "../../src/components/ui/input.jsx";
import { Card } from "../../src/components/ui/card.jsx";
import { MapPin, Loader2 } from "lucide-react";

/**
 * AddressAutocomplete
 *
 * A controlled input with debounced address suggestions from the NSW Planning API.
 *
 * Props:
 * - value: string — the current input value (controlled)
 * - onChange: (nextValue: string) => void — called on every input change and selection
 * - placeholder: string — optional input placeholder
 */
export default function AddressAutocomplete({ value, onChange, placeholder, busy = false }) {
  const [inputValue, setInputValue] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const isUserTypingRef = useRef(false);

  // Keep local state in sync with external value, but do NOT reset typing state
  // or hide suggestions on every keystroke echo from the parent.
  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  // Debounced fetch of address suggestions whenever inputValue changes
  useEffect(() => {
    if (busy) {
      setShowSuggestions(false);
      return undefined;
    }
    // Only fetch when the user is actively typing
    if (!isUserTypingRef.current) {
      return undefined;
    }
    if (!inputValue || inputValue.trim().length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setShowSuggestions(true);

    const controller = new AbortController();
    const timeoutId = setTimeout(async () => {
      try {
        const apiUrl = `https://api.apps1.nsw.gov.au/planning/viewersf/V1/ePlanningApi/address?a=${encodeURIComponent(
          inputValue
        )}&noOfRecords=5`;
        const response = await fetch(apiUrl, { signal: controller.signal });
        const data = await response.json();

        if (Array.isArray(data) && data.length > 0) {
          setSuggestions(data.slice(0, 5));
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Error fetching addresses:", error);
          setSuggestions([]);
          setShowSuggestions(false);
        }
      } finally {
        setLoading(false);
      }
    }, 300);

    // Cleanup pending debounce + abort ongoing request on new input
    return () => {
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [inputValue, busy]);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    isUserTypingRef.current = true;
    if (newValue !== value) {
      onChange(newValue);
    }
  };

  const handleSuggestionClick = (item) => {
    // API returns items with an `address` property (full address string)
    const selected = item?.address || "";
    setInputValue(selected);
    onChange(selected);
    setShowSuggestions(false);
    setSuggestions([]);
    isUserTypingRef.current = false;
  };

  return (
    <div className="relative">
      <div className="relative">
        <Input
          value={inputValue}
          onChange={handleInputChange}
          placeholder={placeholder}
          className="pr-10"
          data-tour="project-address-autocomplete"
          onFocus={() => isUserTypingRef.current && inputValue.trim().length >= 3 && suggestions.length > 0 && setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          autoComplete="off"
        />
        {(busy || loading) && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
          </div>
        )}
      </div>

      {showSuggestions && suggestions.length > 0 && (
        <Card className="absolute z-50 w-full mt-1 max-h-60 overflow-y-auto shadow-lg border border-slate-200 bg-white">
          <div className="p-2">
            {suggestions.map((item, index) => (
              <div
                key={`${item?.propId || "item"}-${index}`}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => handleSuggestionClick(item)}
                className="flex items-center gap-2 p-3 hover:bg-slate-50 cursor-pointer rounded-lg transition-colors"
              >
                <MapPin className="w-4 h-4 text-slate-400 flex-shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-slate-800 truncate">{item?.address || ""}</p>
                  {(item?.postcode || item?.state || item?.locality) ? (
                    <p className="text-sm text-slate-500 truncate">
                      {[item?.locality, item?.state, item?.postcode].filter(Boolean).join(" ")}
                    </p>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}